import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { QaView } from './QaView';
import { ContactsList } from './views/ContactsList';
import { ContactDetail } from './views/ContactDetail';
import { ContactForm } from './views/ContactForm';
import { PipelineView } from './views/PipelineView';
import { RonHome } from './views/RonHome';
import { ManualEngagementsView } from './views/ManualEngagementsView';
import { ActionItemsList } from './views/ActionItemsList';
import { CSVImportFlow } from './views/CSVImportFlow';
import { MergesPlaceholder } from './views/MergesPlaceholder';
import { UploadTranscriptView } from './views/UploadTranscriptView';
import { QaBankReview } from './views/QaBankReview';
import { PlaudReview } from './views/PlaudReview';
import { ProductProposalQueue } from './views/ProductProposalQueue';
import { OpsBoard } from './views/OpsBoard';
import { ProcurementHub } from './views/ProcurementHub';
import { PreflightView } from './views/PreflightView';
import { VinceQueue } from './views/VinceQueue';
import { NateQueue } from './views/NateQueue';
import { ErikQueue } from './views/ErikQueue';
import { ReviewWorkHub } from './views/ReviewWorkHub';
import { SystemToolsHub } from './views/SystemToolsHub';
import { WelcomeView } from './views/WelcomeView';
import { EngineHealthBanner } from './EngineHealthBanner';
import { roleHome } from './role-home';

interface Me {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  department: string;
  role: string;
  jobTitle: string;
}

type State =
  | { kind: 'loading' }
  | { kind: 'anon' }
  | { kind: 'auth'; user: Me }
  | { kind: 'error'; message: string };

export function App(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/me', { credentials: 'include' });
        if (cancelled) return;
        if (res.status === 401) {
          setState({ kind: 'anon' });
          return;
        }
        if (!res.ok) {
          setState({ kind: 'error', message: `Unexpected status ${res.status}` });
          return;
        }
        const user = (await res.json()) as Me;
        setState({ kind: 'auth', user });
      } catch (err) {
        if (cancelled) return;
        setState({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'auth') {
    return <AuthedShell user={state.user} />;
  }

  return (
    <main className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-sm ring-1 ring-slate-200 p-8">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Customer Evidence</h1>
        <p className="mt-1 text-sm text-slate-500">Evidence-backed customer context for Overland Design-Build</p>
        <div className="mt-6">{renderLanding(state)}</div>
      </div>
    </main>
  );
}

function renderLanding(state: State): JSX.Element {
  switch (state.kind) {
    case 'loading':
      return <p className="text-slate-500">Loading…</p>;
    case 'anon':
      return (
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-3 text-xs leading-5 text-slate-600 ring-1 ring-slate-200">
            <p className="font-medium text-slate-700">This is the evidence &amp; review desk.</p>
            <p className="mt-1">
              Reply, call, scope, quote, and close loops in <span className="font-medium text-slate-700">Customer Workbench</span>.
              Verify quote, order, and invoice truth in <span className="font-medium text-slate-700">Odoo</span>.
              Use Sales-QA to find proof, review AI-extracted work, and repair evidence.
            </p>
          </div>
          <a
            href="/auth/google/login"
            className="inline-flex items-center justify-center w-full rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition"
          >
            Sign in with Google
          </a>
        </div>
      );
    case 'error':
      return <p className="text-rose-600 text-sm">Error: {state.message}</p>;
    case 'auth':
      return <p>Authenticated.</p>;
  }
}

const NAV_GROUPS: Array<{
  label: string;
  collapsed?: boolean;
  items: Array<{ path: string; label: string; match?: string[] }>;
}> = [
  {
    label: 'Start here',
    items: [
      { path: '/queues/erik', label: 'Exception review' },
      { path: '/contacts', label: 'Customer evidence' },
      { path: '/qa', label: 'Ask evidence' },
    ],
  },
  {
    label: 'Team queues',
    collapsed: true,
    items: [
      { path: '/queues/vince', label: 'Vince handoff' },
      { path: '/queues/nate', label: 'Nate production' },
      { path: '/ron', label: 'Ron follow-up' },
    ],
  },
  {
    label: 'Review',
    collapsed: true,
    items: [
      {
        path: '/review',
        label: 'Review work',
        match: ['/review', '/action-items', '/qa-bank/review', '/plaud/review', '/transcripts/upload'],
      },
    ],
  },
  {
    label: 'System',
    collapsed: true,
    items: [
      {
        path: '/tools',
        label: 'System tools',
        match: [
          '/tools',
          '/engagements',
          '/ops',
          '/procurement',
          '/product-proposals',
          '/catalog',
          '/pipeline',
          '/imports',
          '/merges',
        ],
      },
    ],
  },
];

function AuthedShell({ user }: { user: Me }): JSX.Element {
  const location = useLocation();
  const isPathActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isItemActive = (item: { path: string; match?: string[] }): boolean =>
    (item.match ?? [item.path]).some((path) => isPathActive(path));

  return (
    <div className="flex min-h-screen flex-col bg-slate-50 md:flex-row">
      <MobileHeader user={user} />
      <aside className="hidden h-screen w-72 shrink-0 flex-col border-r border-slate-200 bg-white md:flex">
        <div className="border-b border-slate-200 px-5 py-4">
          <h1 className="text-base font-semibold tracking-tight text-slate-950">
            Customer Evidence
          </h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Evidence desk, not the quote cockpit.
          </p>
          <a
            href="https://tracker.overlanddesignbuild.com"
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            Open Customer Workbench
          </a>
          <p className="mt-2 text-xs leading-4 text-slate-500">
            Reply, call, scope, quote, and close loops in Customer Workbench. Use this app for proof and review.
          </p>
          <p className="mt-3 truncate text-xs font-medium text-slate-700">
            {user.firstName} {user.lastName}
          </p>
        </div>
        <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-3" aria-label="primary">
          {NAV_GROUPS.map((group) => (
            group.collapsed ? (
              <details
                key={group.label}
                className="group rounded-md border border-slate-200 bg-slate-50/60 px-2 py-1"
                open={group.items.some(isItemActive)}
              >
                <summary className="cursor-pointer list-none py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 marker:hidden">
                  <span>{group.label}</span>
                </summary>
                <div className="mt-1 space-y-0.5 pb-1">
                  {group.items.map((item) => (
                    <NavItem key={item.path} item={item} />
                  ))}
                </div>
              </details>
            ) : (
              <div key={group.label}>
                <div className="px-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {group.label}
                </div>
                <div className="mt-1 space-y-0.5">
                  {group.items.map((item) => (
                    <NavItem key={item.path} item={item} />
                  ))}
                </div>
              </div>
            )
          ))}
        </nav>
        <div className="border-t border-slate-200 px-5 py-3">
          <SignOutButton className="text-xs font-medium text-slate-500 underline hover:text-slate-700" />
        </div>
      </aside>
      <main className="min-w-0 flex-1">
        <EngineHealthBanner />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" key={location.pathname}>
          <Routes>
            <Route path="/" element={<Navigate to={roleHome(user)} replace />} />
            <Route path="/welcome" element={<WelcomeView user={user} />} />
            <Route path="/ron" element={<RonHome />} />
            <Route path="/contacts" element={<ContactsList />} />
            <Route path="/contacts/new" element={<ContactForm mode="create" />} />
            <Route path="/contacts/:id" element={<ContactDetail />} />
            <Route path="/contacts/:id/preflight" element={<PreflightView />} />
            <Route path="/contacts/:id/edit" element={<ContactForm mode="edit" />} />
            <Route path="/review" element={<ReviewWorkHub />} />
            <Route path="/tools" element={<SystemToolsHub />} />
            <Route path="/pipeline" element={<PipelineView />} />
            <Route path="/engagements" element={<ManualEngagementsView />} />
            <Route path="/action-items" element={<ActionItemsList />} />
            <Route path="/queues/vince" element={<VinceQueue />} />
            <Route path="/queues/nate" element={<NateQueue />} />
            <Route path="/queues/erik" element={<ErikQueue />} />
            <Route path="/transcripts/upload" element={<UploadTranscriptView />} />
            <Route path="/qa" element={<QaView />} />
            <Route path="/qa-bank/review" element={<QaBankReview />} />
            <Route path="/plaud/review" element={<PlaudReview />} />
            <Route path="/product-proposals" element={<ProductProposalQueue />} />
            <Route path="/catalog" element={<Navigate to="/product-proposals" replace />} />
            <Route path="/ops" element={<OpsBoard />} />
            <Route path="/procurement" element={<ProcurementHub />} />
            <Route path="/imports" element={<CSVImportFlow />} />
            <Route path="/merges" element={<MergesPlaceholder />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function MobileHeader({ user }: { user: Me }): JSX.Element {
  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white md:hidden">
      <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-base font-semibold tracking-tight text-slate-950">
              Customer Evidence
            </h1>
            <p className="mt-0.5 text-xs text-slate-500">
              Evidence desk, not the quote cockpit.
            </p>
            <p className="mt-2 truncate text-xs font-medium text-slate-700">
              {user.firstName} {user.lastName}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <a
              href="https://tracker.overlanddesignbuild.com"
              target="_blank"
              rel="noreferrer"
              title="Leaving Sales-QA evidence. Customer Workbench owns the next action — reply, call, scope, quote, close the loop."
              className="rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              Open Workbench
            </a>
            <SignOutButton className="text-xs font-medium text-slate-500 underline hover:text-slate-700" />
          </div>
        </div>
      </div>
      <nav className="border-t border-slate-100 px-3 py-2" aria-label="primary">
        <div className="space-y-2">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="flex flex-wrap items-center gap-1.5">
              <span className="whitespace-nowrap px-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                {group.label}
              </span>
              {group.items.map((item) => (
                <MobileNavItem key={item.path} item={item} />
              ))}
            </div>
          ))}
        </div>
      </nav>
    </header>
  );
}

function SignOutButton({ className }: { className: string }): JSX.Element {
  return (
    <button
      type="button"
      onClick={async () => {
        await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
        window.location.href = '/';
      }}
      className={className}
    >
      Sign out
    </button>
  );
}

function NavItem({ item }: { item: { path: string; label: string; match?: string[] } }): JSX.Element {
  const location = useLocation();
  const isActive = (item.match ?? [item.path]).some((path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  return (
    <NavLink
      to={item.path}
      className={() =>
        `block rounded-md px-2.5 py-1.5 text-sm transition ${
          isActive
            ? 'bg-slate-950 text-white shadow-sm'
            : 'text-slate-700 hover:bg-slate-100 hover:text-slate-950'
        }`
      }
    >
      <span className="block font-medium leading-5">{item.label}</span>
    </NavLink>
  );
}

function MobileNavItem({ item }: { item: { path: string; label: string; match?: string[] } }): JSX.Element {
  const location = useLocation();
  const isActive = (item.match ?? [item.path]).some((path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`),
  );

  return (
    <NavLink
      to={item.path}
      className={() =>
        `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium transition ${
          isActive
            ? 'bg-slate-950 text-white shadow-sm'
            : 'bg-slate-100 text-slate-700 hover:bg-slate-200 hover:text-slate-950'
        }`
      }
    >
      {item.label}
    </NavLink>
  );
}

function NotFound(): JSX.Element {
  return (
    <div className="rounded-lg bg-white p-8 ring-1 ring-slate-200">
      <h2 className="text-lg font-semibold text-slate-900">Not found</h2>
      <p className="mt-1 text-sm text-slate-500">This page doesn't exist.</p>
    </div>
  );
}
